import { createAuthClient } from 'better-auth/react';
import {
  adminClient,
  jwtClient,
  phoneNumberClient,
  twoFactorClient,
} from 'better-auth/client/plugins';

import { crossSignOut } from './bridge';
import { env } from './env';

/**
 * The one auth Worker's client (`admin-is-an-app` D1): `/api/auth/*`, called with the
 * browser's session for that origin — the hosts are one site, so the cookie goes, and the
 * Worker answers this origin with credentialed CORS (`services/auth/src/app-origins.ts`).
 * Staff sign in here (`staff-sign-in-here` D1), but there is still no admin auth: this
 * client holds no secret and mints nothing.
 *
 * Where it dials is its own variable (D2): a dev server proxying `/api` is same-origin, so
 * the host-only cookie holds on localhost and no CORS applies. `VITE_WEB_ORIGIN` keeps its
 * other two jobs, the sign-out destination and View as.
 *
 * Under `VITE_SESSION_BRIDGE` it dials THIS origin, whose Worker forwards those paths to
 * the same auth Worker (`the-session-crosses-to-tutero-com` D10). That is the whole point:
 * the cookie is host-only, so a session read against another host's origin can never
 * become a session of this host's, and once the two hosts are on different registrable
 * domains it cannot even be read. An explicit `VITE_AUTH_ORIGIN` still wins, so the dev
 * server's proxy is unaffected.
 */
export const authClient = createAuthClient({
  baseURL:
    env.VITE_AUTH_ORIGIN ??
    (env.VITE_SESSION_BRIDGE ? window.location.origin : env.VITE_WEB_ORIGIN),
  fetchOptions: { credentials: 'include' },
  /* `phoneNumberClient` for Settings' texted factor: the Worker has served the plugin since
     `a-text-is-a-second-factor`, and without it there is no `phoneNumber.*` to call (`settings-is-a-dialog` D9). */
  plugins: [adminClient(), jwtClient(), twoFactorClient(), phoneNumberClient()],
});

/** A refused Better Auth call: its machine code, and the status where it carried none. */
interface AuthApiError {
  code?: string | undefined;
  message?: string | undefined;
  status: number;
}

/** The sign-in surface's own codes, so no component branches on the Worker's (D3). */
export type AuthErrorCode =
  | 'invalid-credential'
  | 'user-not-found'
  | 'invalid-email'
  | 'weak-password'
  /* Asking for access alone: the one form in the admin that creates an account (`request-access-is-a-page` D6). */
  | 'email-already-in-use'
  | 'too-many-requests'
  | 'operation-not-allowed'
  | 'requires-recent-login'
  /* Settings alone: the field in front of the person is a password and nothing else (`settings-is-a-dialog` D9). */
  | 'wrong-password'
  | 'network'
  /* The password was right and a factor is owed: nothing the staff member did (D4). */
  | 'multi-factor-required'
  | 'invalid-code'
  | 'unknown';

/** What the code step needs: the factors the account has, as the Worker names them. */
export interface SecondFactorChallenge {
  readonly methods: readonly string[];
  /** Why a text-only account's code did not go, for the step to open on. */
  readonly refused?: string;
}

export class AuthError extends Error {
  readonly code: AuthErrorCode;
  /** The thrown value, for logging; never shown. */
  override readonly cause: unknown;
  /** Set on `multi-factor-required` alone. */
  readonly challenge?: SecondFactorChallenge;

  constructor(
    code: AuthErrorCode,
    message: string,
    cause?: unknown,
    challenge?: SecondFactorChallenge,
  ) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.cause = cause;
    this.challenge = challenge;
  }
}

/* A wrong password and an unknown address answer alike, so both read alike (D3). */
const CODE_MAP: Record<string, AuthErrorCode> = {
  INVALID_EMAIL_OR_PASSWORD: 'invalid-credential',
  INVALID_PASSWORD: 'invalid-credential',
  USER_NOT_FOUND: 'user-not-found',
  CREDENTIAL_ACCOUNT_NOT_FOUND: 'operation-not-allowed',
  NO_PASSWORD: 'operation-not-allowed',
  INVALID_EMAIL: 'invalid-email',
  PASSWORD_TOO_SHORT: 'weak-password',
  PASSWORD_TOO_LONG: 'weak-password',
  USER_ALREADY_EXISTS: 'email-already-in-use',
  SESSION_EXPIRED: 'requires-recent-login',
  SESSION_NOT_FRESH: 'requires-recent-login',
  INVALID_TWO_FACTOR_COOKIE: 'requires-recent-login',
  INVALID_CODE: 'invalid-code',
  INVALID_BACKUP_CODE: 'invalid-code',
  OTP_HAS_EXPIRED: 'invalid-code',
  OTP_EXPIRED: 'invalid-code',
  OTP_NOT_FOUND: 'invalid-code',
  INVALID_OTP: 'invalid-code',
  TOO_MANY_ATTEMPTS: 'too-many-requests',
  TOO_MANY_ATTEMPTS_REQUEST_NEW_CODE: 'too-many-requests',
  TOO_MANY_TEXTS: 'too-many-requests',
  ACCOUNT_TEMPORARILY_LOCKED: 'too-many-requests',
  TEXT_IS_THE_SECOND_STEP: 'operation-not-allowed',
};

const MESSAGES: Record<AuthErrorCode, string> = {
  'invalid-credential': 'Incorrect email or password.',
  'user-not-found': 'No account found for that email.',
  'invalid-email': 'Enter a valid email address.',
  'weak-password': 'Password must be at least 6 characters.',
  /* Says nothing about whether the address is staff: a wrong password and an unknown address answer alike (D3). */
  'email-already-in-use':
    'That email already has a Tutero account. Log in instead.',
  'too-many-requests': 'Too many attempts. Try again in a few minutes.',
  'operation-not-allowed': 'That sign-in method is not enabled.',
  'requires-recent-login': 'Please sign in again to complete this action.',
  'wrong-password': 'Your password is wrong.',
  network: 'Network error. Check your connection and try again.',
  'multi-factor-required':
    'This account uses two-factor authentication. Enter your verification code.',
  'invalid-code': 'That code is not right. Check it and try again.',
  unknown: 'Something went wrong. Please try again.',
};

/* Only the Worker knows when the next code may be asked for, or what must come first. */
const WORKERS_WORDS = new Set(['TOO_MANY_TEXTS', 'TEXT_IS_THE_SECOND_STEP']);

function isAuthApiError(err: unknown): err is AuthApiError {
  return (
    typeof err === 'object' &&
    err !== null &&
    'status' in err &&
    typeof (err as { status: unknown }).status === 'number'
  );
}

/** The Worker's own machine code, for a caller that must tell two refusals `CODE_MAP` reads alike apart. */
export function authErrorCode(err: unknown): string | undefined {
  return isAuthApiError(err) ? err.code : undefined;
}

/** Anything thrown by an auth call, as a code and a sentence the form renders (D3). */
export function toAuthError(err: unknown): AuthError {
  if (err instanceof AuthError) return err;
  let code: AuthErrorCode = 'unknown';
  if (isAuthApiError(err)) {
    const mapped = err.code ? CODE_MAP[err.code] : undefined;
    if (mapped) code = mapped;
    else if (err.status === 429) code = 'too-many-requests';
    else if (err.status === 401) code = 'invalid-credential';
    /* No status the Worker sent: the fetch itself failed. */ else if (
      err.status === 0
    )
      code = 'network';
  } else if (err instanceof TypeError) {
    code = 'network';
  }
  const words =
    isAuthApiError(err) && err.code && WORKERS_WORDS.has(err.code)
      ? err.message
      : undefined;
  return new AuthError(code, words ?? MESSAGES[code], err);
}

/** The answer an enrolled account's password sign-in gives: no session, a factor owed. */
function secondFactorOwed(data: unknown): readonly string[] | null {
  if (typeof data !== 'object' || data === null) return null;
  const answer = data as {
    twoFactorRedirect?: unknown;
    twoFactorMethods?: unknown;
  };
  if (answer.twoFactorRedirect !== true) return null;
  return Array.isArray(answer.twoFactorMethods)
    ? answer.twoFactorMethods.filter(
        (method): method is string => typeof method === 'string',
      )
    : ['totp'];
}

/** Sign a staff member in. A second factor owed throws `multi-factor-required` (D4). */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<void> {
  try {
    const { data, error } = await authClient.signIn.email({ email, password });
    if (error) throw error;
    const methods = secondFactorOwed(data);
    if (methods === null) return;
    /* A texted code is the only factor: it goes now, and a refused send opens the step saying so. */
    const refused =
      methods.includes('otp') && !methods.includes('totp')
        ? await sendSecondFactorCode().then(
            () => undefined,
            (err: unknown) => toAuthError(err).message,
          )
        : undefined;
    throw new AuthError(
      'multi-factor-required',
      MESSAGES['multi-factor-required'],
      undefined,
      { methods, ...(refused === undefined ? {} : { refused }) },
    );
  } catch (err) {
    throw toAuthError(err);
  }
}

/** What asking for admin access creates an account from (`request-access-is-a-page` D5). */
export interface SignUp {
  /** The whole name, as the account holds one name; the backend splits it if it ever must. */
  name: string;
  email: string;
  password: string;
}

/**
 * Create an account on the one auth Worker, for someone asking for admin access
 * (`request-access-is-a-page` D1). `emailAndPassword` is enabled there with no
 * `disableSignUp`, and `emailVerification.sendOnSignUp` mails the link, so there is no
 * second call to send it and nothing to drop on the floor.
 *
 * An address already in use is one sentence and a stop (D6). Flutter answers it by signing
 * the person in with the password they typed and reading the `admin` claim
 * (`admin_access_bloc.dart:78-80`, `:176-182`), which turns a public form into a
 * credential-probe oracle; nothing here signs anybody in.
 */
export async function signUpWithEmail({
  name,
  email,
  password,
}: SignUp): Promise<void> {
  try {
    const { error } = await authClient.signUp.email({ email, password, name });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

/** Finish a sign-in the Worker answered with a challenge; the right code issues the session. */
export async function completeSecondFactor(
  code: string,
  kind: 'totp' | 'text' | 'backup' = 'totp',
): Promise<void> {
  try {
    const { error } =
      kind === 'backup'
        ? await authClient.twoFactor.verifyBackupCode({ code })
        : kind === 'text'
          ? await authClient.twoFactor.verifyOtp({ code })
          : await authClient.twoFactor.verifyTotp({ code });
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

/** Text the pending sign-in's code; past the send limit the Worker says when to retry. */
export async function sendSecondFactorCode(): Promise<void> {
  try {
    const { error } = await authClient.twoFactor.sendOtp();
    if (error) throw error;
  } catch (err) {
    throw toAuthError(err);
  }
}

/** A refusal from the auth Worker's admin API, carrying the machine code it sent (`staff-holds-the-grants` D6). */
export class AdminError extends Error {
  readonly code: string | undefined;
  readonly status: number;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'AdminError';
    this.status = status;
    this.code = code;
  }
}

/** Whatever `$fetch` handed back as `error`, read by shape: the body's `code` rides beside the status. */
export function toAdminError(error: unknown): AdminError {
  const refusal = (error ?? {}) as Record<string, unknown>;
  const words = [refusal.message, refusal.statusText].find(
    (value): value is string => typeof value === 'string' && value !== '',
  );
  return new AdminError(
    typeof refusal.status === 'number' ? refusal.status : 0,
    words ?? 'The auth Worker refused it.',
    typeof refusal.code === 'string' ? refusal.code : undefined,
  );
}

/** An admin as `GET /admin/permissions` answers: the account, joined to its `adminGrant` row's list. */
export interface AdminGrants {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  permissions: readonly string[];
}

/** Every admin and what each holds. Here and not in the Staff tool because the shell needs it too (D7). */
export async function adminGrants(): Promise<AdminGrants[]> {
  const { data, error } = await authClient.$fetch<{ admins: AdminGrants[] }>(
    '/admin/permissions',
    { method: 'GET' },
  );
  if (error) throw toAdminError(error);
  return data?.admins ?? [];
}

/** A signed-in staff member, as the shell and every tool read them. */
export interface Staff {
  id: string;
  name: string;
  email: string;
  /** A staff role (`isStaff`); anything else is a teacher who reached the wrong site. */
  role: string | null;
  /** Their grants, `<tool>:<action>`: the token's claim where it carries one, else their `adminGrant` row (D7). */
  perms: readonly string[];
  /** The region the edge routes them to, which the picker defaults to (`manifests-draw-the-nav` D9). */
  country: string | null;
  /** The picture they are drawn with, else the initial; on the session already, so it costs no request (`a-staff-member-has-a-face` D5). */
  image: string | null;
}

/** The token's claims the shell reads. Unverified here: each backend verifies its own. */
interface Claims {
  exp?: number;
  perms?: unknown;
  country?: unknown;
}

function claimsOf(token: string): Claims {
  try {
    const payload = token.split('.')[1] ?? '';
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as Claims;
  } catch {
    return {};
  }
}

let held: { token: string; exp: number } | null = null;

function hold(token: string | null | undefined) {
  if (!token) return;
  held = { token, exp: claimsOf(token).exp ?? 0 };
}

/** The caller's own grants, read from the admins list; a refusal leaves them signed in with none (D8). */
async function grantsOf(
  id: string,
  role: string | null,
): Promise<readonly string[]> {
  /* `/admin/permissions` answers a teacher 403, and the shell is about to refuse them anyway. */
  if (!isStaff(role)) return [];
  try {
    return (
      (await adminGrants()).find((admin) => admin.id === id)?.permissions ?? []
    );
  } catch {
    return [];
  }
}

/** The signed-in staff member, or `null` for no session or a visitor's; the token rides on the answer (`set-auth-jwt`). */
export async function loadStaff(): Promise<Staff | null> {
  /* Only this answer's token speaks for this session: another tab may have changed it. */
  held = null;
  let token: string | null = null;
  const { data } = await authClient.getSession({
    fetchOptions: {
      onSuccess: (context) => {
        token = context.response.headers.get('set-auth-jwt');
      },
    },
  });
  hold(token);
  const user = data?.user as
    | {
        id: string;
        name: string;
        email: string;
        isAnonymous?: boolean | null;
        role?: string | null;
        image?: string | null;
      }
    | undefined;
  if (!user || user.isAnonymous) return null;
  const claims = token ? claimsOf(token) : {};
  const claimed = claims.perms;
  /* The claim wins the day `staff-access` lands; until then `tokenClaims()` emits none and the row speaks (D7). */
  const perms = Array.isArray(claimed)
    ? claimed.filter((perm): perm is string => typeof perm === 'string')
    : await grantsOf(user.id, user.role ?? null);
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role ?? null,
    perms,
    country: typeof claims.country === 'string' ? claims.country : null,
    /* An empty string is no picture: `NameAvatar` would draw a broken image for one. */
    image:
      typeof user.image === 'string' && user.image !== '' ? user.image : null,
  };
}

/** The API token for the graph, minted again a minute before it lapses. */
export async function apiToken(): Promise<string> {
  if (held && held.exp * 1000 - Date.now() > 60_000) return held.token;
  const { data, error } = await authClient.token();
  if (error || !data?.token) throw new Error('No API token: sign in again');
  hold(data.token);
  return data.token;
}

/**
 * Sign out of the one session, and leave for the admin's own front door and its login
 * form.
 *
 * Under `VITE_SESSION_BRIDGE` that is a chain of navigations rather than a call: each host
 * holds a session of its own, a cookie can only be cleared by the host that set it, and a
 * cross-site `Set-Cookie` is dropped by Safari and partitioned by Firefox. So each hop
 * clears first-party and redirects on, and the chain ends on the host it did not start
 * on — so it starts on `VITE_WEB_ORIGIN` to end here (`bridge.ts` › `crossSignOut`).
 * Without the bridge the session is this host's alone, and `authClient.signOut` is the
 * whole of it; that path keeps leaving for `VITE_WEB_ORIGIN`, where it always went.
 *
 * Clearing one host alone is not a thing that works: the next load of the admin would ask
 * the partner for a session and pull the same one straight back, so sign-out would appear
 * not to have happened and there would be no way out but clearing cookies by hand. The
 * client's own `signOut` is not called beside the chain either — it is the same deletion,
 * raced against a navigation that usually wins.
 */
export async function signOut(): Promise<void> {
  if (env.VITE_SESSION_BRIDGE) {
    held = null;
    crossSignOut(env.VITE_WEB_ORIGIN);
    return;
  }
  try {
    await authClient.signOut();
  } finally {
    held = null;
    window.location.assign(env.VITE_WEB_ORIGIN);
  }
}

/**
 * The roles that reach the admin, as the auth Worker names them (MathGaps/schools
 * `services/auth/src/impersonation.ts` › `ADMIN_ROLES`). Oso's policy is `"admin" if
 * "superadmin"` (`policy/base.polar`), so a superadmin is an admin and the shell says so
 * rather than refusing the more privileged of the two. The gate is convenience either way:
 * every backend refuses what the caller's grants do not hold (A4).
 */
const STAFF_ROLES = ['admin', 'superadmin'];

export function isStaff(role: string | null | undefined): boolean {
  return role != null && STAFF_ROLES.includes(role);
}
